import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
  type ISignUpResult,
} from 'amazon-cognito-identity-js';

import type { RuntimeConfig } from '../config.js';

/**
 * Cognito, behind an interface this application can reason about (M1.14.2).
 *
 * ## Why a library rather than raw calls
 *
 * The user pool allows **only** `USER_SRP_AUTH` ([M0.3.2](../../../Backlog/M0-Foundations.md)),
 * so signing in means the Secure Remote Password exchange: big-integer
 * arithmetic, HKDF, and a signature over a padded hash. Hand-writing that is
 * several hundred lines of cryptography whose failure mode is "authentication
 * that appears to work", and weakening the pool to `USER_PASSWORD_AUTH` to
 * avoid writing it would send the password to Cognito in the clear inside TLS
 * — a worse trade for a worse reason.
 *
 * `amazon-cognito-identity-js` is the library AWS publishes for exactly this.
 * It is also callback-based and throws strings as often as errors, which is
 * why nothing outside this module touches it: everything below returns a
 * promise and a typed failure.
 *
 * ## The ID token is the one that travels
 *
 * API Gateway's JWT authorizer is configured with `jwtAudience: [webClientId]`
 * ([M0.4.1](../../../Backlog/M0-Foundations.md)). Cognito puts the client id in
 * `aud` on the **ID** token and in `client_id` on the access token, so an
 * access token fails the audience check with a 401 that says nothing useful.
 */

export type AuthErrorKind =
  | 'invalid-credentials'
  | 'unconfirmed'
  | 'user-exists'
  | 'invalid-code'
  | 'expired-code'
  | 'weak-password'
  | 'too-many-attempts'
  | 'network'
  | 'unknown';

/**
 * A failure with a kind the UI can branch on.
 *
 * The message is Cognito's, kept for the console rather than for the screen:
 * the screens render their own wording from `kind`, because "Incorrect
 * username or password." is not what to tell somebody who has mistyped an
 * email address.
 */
export class AuthError extends Error {
  constructor(
    readonly kind: AuthErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

export interface Session {
  readonly idToken: string;
  /** Epoch seconds. */
  readonly expiresAt: number;
  readonly email: string;
}

export interface AuthClient {
  signUp(email: string, password: string): Promise<{ confirmed: boolean }>;
  confirmSignUp(email: string, code: string): Promise<void>;
  resendCode(email: string): Promise<void>;
  signIn(email: string, password: string): Promise<Session>;
  forgotPassword(email: string): Promise<void>;
  confirmForgotPassword(email: string, code: string, password: string): Promise<void>;
  /** The stored session, refreshed if it is close to expiry. Undefined when signed out. */
  currentSession(options?: { forceRefresh?: boolean }): Promise<Session | undefined>;
  signOut(): void;
}

/**
 * How long before expiry a token is refreshed.
 *
 * Tokens last an hour. Five minutes is long enough that a request in flight
 * cannot be caught by the expiry it just checked, and short enough that a
 * session left open overnight refreshes a handful of times rather than
 * constantly.
 */
export const REFRESH_MARGIN_SECONDS = 5 * 60;

export function createAuthClient(config: RuntimeConfig): AuthClient {
  const pool = new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.webClientId,
  });

  const userFor = (email: string): CognitoUser =>
    new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool });

  return {
    async signUp(email, password) {
      const result = await new Promise<ISignUpResult>((resolve, reject) => {
        pool.signUp(email.trim().toLowerCase(), password, [], [], (error, value) => {
          if (error !== null && error !== undefined) {
            reject(toAuthError(error));
            return;
          }
          resolve(value as ISignUpResult);
        });
      });

      return { confirmed: result.userConfirmed === true };
    },

    async confirmSignUp(email, code) {
      await new Promise<void>((resolve, reject) => {
        userFor(email).confirmRegistration(code.trim(), true, (error) => {
          if (error !== null && error !== undefined) {
            reject(toAuthError(error));
            return;
          }
          resolve();
        });
      });
    },

    async resendCode(email) {
      await new Promise<void>((resolve, reject) => {
        userFor(email).resendConfirmationCode((error) => {
          if (error !== null && error !== undefined) {
            reject(toAuthError(error));
            return;
          }
          resolve();
        });
      });
    },

    async signIn(email, password) {
      const user = userFor(email);

      const session = await new Promise<CognitoUserSession>((resolve, reject) => {
        user.authenticateUser(
          new AuthenticationDetails({
            Username: email.trim().toLowerCase(),
            Password: password,
          }),
          {
            onSuccess: resolve,
            onFailure: (error: unknown) => {
              reject(toAuthError(error));
            },
          },
        );
      });

      return toSession(session);
    },

    async forgotPassword(email) {
      await new Promise<void>((resolve, reject) => {
        userFor(email).forgotPassword({
          onSuccess: () => {
            resolve();
          },
          onFailure: (error: unknown) => {
            reject(toAuthError(error));
          },
        });
      });
    },

    async confirmForgotPassword(email, code, password) {
      await new Promise<void>((resolve, reject) => {
        userFor(email).confirmPassword(code.trim(), password, {
          onSuccess: () => {
            resolve();
          },
          onFailure: (error: unknown) => {
            reject(toAuthError(error));
          },
        });
      });
    },

    async currentSession(options) {
      const user = pool.getCurrentUser();
      if (user === null) {
        return undefined;
      }

      const session = await new Promise<CognitoUserSession | undefined>((resolve) => {
        user.getSession((error: unknown, value: CognitoUserSession | null) => {
          /*
           * `getSession` refreshes with the stored refresh token when the ID
           * token has expired, so this one call covers both "still valid" and
           * "renew it". A failure here means the refresh token is gone or
           * revoked, which is a signed-out user rather than an error worth
           * showing.
           */
          resolve(
            error !== null && error !== undefined ? undefined : (value ?? undefined),
          );
        });
      });

      if (session === undefined || !session.isValid()) {
        return undefined;
      }

      const current = toSession(session);

      if (
        options?.forceRefresh !== true &&
        current.expiresAt - Math.floor(Date.now() / 1000) > REFRESH_MARGIN_SECONDS
      ) {
        return current;
      }

      const refreshed = await new Promise<CognitoUserSession | undefined>((resolve) => {
        user.refreshSession(session.getRefreshToken(), (error, value) => {
          resolve(error !== null && error !== undefined ? undefined : value);
        });
      });

      return refreshed === undefined ? current : toSession(refreshed);
    },

    signOut() {
      pool.getCurrentUser()?.signOut();
    },
  };
}

function toSession(session: CognitoUserSession): Session {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload() as { email?: string; exp?: number };

  return {
    idToken: idToken.getJwtToken(),
    expiresAt: typeof payload.exp === 'number' ? payload.exp : 0,
    email: payload.email ?? '',
  };
}

/**
 * Cognito's error names, mapped to something the UI can act on.
 *
 * The library throws `Error`s with a `code`, plain objects, and occasionally
 * strings, so this takes `unknown` deliberately.
 */
export function toAuthError(error: unknown): AuthError {
  const name =
    typeof error === 'object' && error !== null
      ? String(
          (error as { code?: unknown; name?: unknown }).code ??
            (error as { name?: unknown }).name ??
            '',
        )
      : '';

  const message =
    typeof error === 'object' && error !== null
      ? String((error as { message?: unknown }).message ?? name)
      : String(error);

  switch (name) {
    case 'NotAuthorizedException':
      return new AuthError('invalid-credentials', message);
    case 'UserNotFoundException':
      /*
       * Reported as bad credentials, not as "no such account". Distinguishing
       * them turns the sign-in form into an oracle for which email addresses
       * have accounts here.
       */
      return new AuthError('invalid-credentials', message);
    case 'UserNotConfirmedException':
      return new AuthError('unconfirmed', message);
    case 'UsernameExistsException':
      return new AuthError('user-exists', message);
    case 'CodeMismatchException':
      return new AuthError('invalid-code', message);
    case 'ExpiredCodeException':
      return new AuthError('expired-code', message);
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      return new AuthError('weak-password', message);
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return new AuthError('too-many-attempts', message);
    case 'NetworkError':
      return new AuthError('network', message);
    default:
      return new AuthError('unknown', message);
  }
}
